import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Search, LogOut, Sparkles, Shield, ArrowRight } from 'lucide-react';
import { useAppDispatch } from '@/data/store/utils/redux';
import { sessionLogout } from '@/data/store/actions';
import { getCurrentSessionUser } from '@/data/store/selectors';

interface OrganizationsHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  // Removed organizationsCount prop since we can't know total with pagination
}

const OrganizationsHeader: React.FC<OrganizationsHeaderProps> = ({
  searchQuery,
  onSearchChange,
}) => {
  const dispatch = useAppDispatch();
  const user = useSelector(getCurrentSessionUser);
  // Note: the backend returns 'developer' field according to requirements
  const isDeveloper = (user as any)?.developer === true;

  const handleLogout = () => {
    dispatch(sessionLogout());
  };

  return (
    <motion.header
      className="relative px-8 py-12"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      {/* Background Gradient Accent */}
      <div className="absolute top-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />

      {/* Main Header Content */}
      <div className="relative">
        {/* Top Row */}
        <div className="mb-12 flex items-start justify-between">
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {/* Icon & Title */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25">
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-green-400 shadow-sm" />
              </div>

              <div>
                <h1 className="text-4xl font-bold tracking-tight text-gray-900">Επιλογή Οργανισμού / Έργου</h1>
                <div className="mt-1 flex items-center gap-2">
                  <div className="h-px w-12 bg-gradient-to-r from-blue-500 to-purple-500" />
                  <span className="text-sm font-medium text-blue-600">Energy Portal</span>
                </div>
              </div>
            </div>

            {/* Subtitle */}
            <p className="max-w-2xl text-lg leading-relaxed text-gray-600">
              Επιλέξτε έναν οργανισμό ή έργο για να αποκτήσετε πρόσβαση στο ταμπλό διαχείρισης ενέργειας
            </p>
          </motion.div>

          {/* Logout Button */}
          <motion.button
            onClick={handleLogout}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            className="group relative rounded-2xl bg-gray-50 p-3 transition-all duration-300 hover:bg-gray-100"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-white p-2 shadow-sm transition-all duration-300 group-hover:shadow-md">
                <LogOut className="h-4 w-4 text-gray-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">Αποσύνδεση</span>
            </div>
          </motion.button>
        </div>

        {/* Search Section */}
        <motion.div
          className="flex items-center justify-between"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {/* Enhanced Search */}
          <div className="relative max-w-md flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Αναζήτηση οργανισμών / έργων..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-2xl border-0 bg-gray-50 py-4 pr-4 pl-12 text-gray-900 placeholder-gray-500 shadow-sm ring-1 ring-gray-200 transition-all duration-300 hover:bg-gray-100/50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 focus:outline-none"
            />
            {searchQuery && (
              <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                <div className="h-2 w-2 animate-pulse rounded-full bg-blue-500" />
              </div>
            )}
          </div>

          {/* CAS Admin entry: the dashboard spans all organizations, so it doesn't need one selected */}
          {isDeveloper && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                to="/admin"
                className="group relative flex items-center gap-4 overflow-hidden rounded-2xl bg-gradient-to-br from-purple-50 via-white to-indigo-50 py-3 pr-5 pl-3 shadow-sm ring-1 ring-purple-100 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10 hover:ring-purple-200"
              >
                <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 shadow-md shadow-purple-500/25">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div className="relative">
                  <div className="text-sm font-semibold text-gray-900">Admin Mode</div>
                  <div className="text-xs font-medium text-purple-600">CAS Dashboard</div>
                </div>
                <ArrowRight className="relative ml-2 h-4 w-4 text-purple-400 transition-all duration-300 group-hover:translate-x-1 group-hover:text-purple-600" />
              </Link>
            </motion.div>
          )}
        </motion.div>
      </div>
    </motion.header>
  );
};

export default OrganizationsHeader;